import { memo } from 'react';

import { usePublicShowcaseNavEntries } from '~/components/v2/components/showcase/hooks/usePublicShowcaseNavEntries';
import { PublicShowcaseNavLink } from '~/components/v2/components/showcase/PublicShowcaseNavLink';
import { PublicShowcaseNavPageEntry } from '~/components/v2/components/showcase/PublicShowcaseNavPageEntry';

/**
 * The public navigation bar — Home plus the link's authored sub-pages and external links. Desktop
 * only: below the medium breakpoint the same destinations render from the workspace header's
 * hamburger menu (`PublicShowcaseNavMenu`), so this bar hides there rather than showing both.
 *
 * It renders inside the page's content column (already width-constrained by the column) and scrolls
 * with the content: the workspace header above it is the one pinned element, so a second sticky bar
 * below it would double-pin. It carries no stacking context of its own — the board dropdowns are
 * portaled (`z-portal`), so they layer above page content without the bar needing a z-index.
 *
 * Fully smart: it reads its own context, so the page threads nothing. When the link has no
 * sub-pages — or navigation is hidden — it renders nothing and the page is byte-identical to
 * before sub-pages existed.
 */
export const PublicShowcaseNav = memo(() => {
  const { entries, isVisible } = usePublicShowcaseNavEntries();

  if (!isVisible) {
    return null;
  }

  return (
    // No background of its own: the nav scrolls inside the gray-1 page column, so it would only
    // repaint the same surface — but as the column's top child (when there is no cover) a full-width
    // opaque block squares off the column's rounded top corners, filling the radius cut-outs. Staying
    // transparent lets the column's own rounded gray-1 show through at the corners; the `border-b` is
    // a line at the nav's BOTTOM, nowhere near the corners, so it is unaffected. Harmless when a cover
    // sits above the nav — the nav is no longer the top child there.
    <div
      className="hidden w-full border-b ads:border-b-default border-b-gray-4 md:block"
      data-testid="PUBLIC_COLLECTION_NAV"
    >
      {/*
       * px-4 matches the page header's gutter so the entries line up with the collection title.
       * overflow-y-hidden pairs with overflow-x-auto: with one axis non-visible CSS computes the
       * other to `auto`, so the 1px the active underline's `-mb-px` overflows would otherwise raise
       * a stray vertical scrollbar. Clipping Y kills it while keeping the horizontal scroll for many
       * entries. The board dropdowns are portaled, so this never clips them.
       */}
      <nav aria-label="Showcase pages" className="flex items-end gap-4 overflow-x-auto overflow-y-hidden px-4">
        {entries.map((entry) =>
          // External links never list boards — they render the plain anchor and skip the board
          // query entirely. Page entries (Home + collection sub-pages) own their own board dropdown.
          entry.isExternal ? (
            <PublicShowcaseNavLink key={entry.id} entry={entry} />
          ) : (
            <PublicShowcaseNavPageEntry key={entry.id} entry={entry} />
          ),
        )}
      </nav>
    </div>
  );
});

PublicShowcaseNav.displayName = 'PublicShowcaseNav';
